import { useEffect, useRef, useState } from 'react';

const LINE_1 = "There's nothing left here";
const LINE_2 = "We're sorry";

export function DispatchedScreen({
  onSignOut,
}: {
  onSignOut?: () => void;
}) {
  return (
    <>
      <style>{SHARED_CSS}</style>
      <EmbersVariant />
      {onSignOut ? (
        <div className="ds-signout-wrap">
          <button className="ds-signout" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      ) : null}
    </>
  );
}

function Typewriter({ startDelayMs }: { startDelayMs: number }) {
  const [l1, setL1] = useState('');
  const [l2, setL2] = useState('');
  const [showCursor2, setShowCursor2] = useState(false);

  useEffect(() => {
    const timeouts: number[] = [];
    const perChar = 65;
    const betweenLines = 700;

    const startAt = (idx: number, fn: () => void, base: number) => {
      const id = window.setTimeout(fn, base + idx * perChar);
      timeouts.push(id);
    };

    for (let i = 1; i <= LINE_1.length; i++) {
      startAt(i, () => setL1(LINE_1.slice(0, i)), startDelayMs);
    }
    const afterL1 = startDelayMs + LINE_1.length * perChar + betweenLines;
    timeouts.push(window.setTimeout(() => setShowCursor2(true), afterL1 - 150));
    for (let i = 1; i <= LINE_2.length; i++) {
      startAt(i, () => setL2(LINE_2.slice(0, i)), afterL1);
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [startDelayMs]);

  const l1Done = l1.length === LINE_1.length;
  const l2Done = l2.length === LINE_2.length;

  return (
    <div className="ds-text">
      <div className="ds-line ds-l1">
        <span>{l1}</span>
        <span className={`ds-caret ${l1Done && showCursor2 ? 'hide' : ''}`} />
      </div>
      <div className="ds-line ds-l2">
        <span>{l2}</span>
        <span className={`ds-caret ${!showCursor2 || l2Done ? 'hide' : ''}`} />
      </div>
    </div>
  );
}

function EmbersVariant() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    type Ember = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      life: number;
      maxLife: number;
      hue: number;
      flicker: number;
    };
    const embers: Ember[] = [];

    const spawn = () => {
      const r = 0.6 + Math.random() * 2.1;
      embers.push({
        x: Math.random() * w,
        y: h + 10,
        vx: (Math.random() - 0.5) * 0.25,
        vy: -(0.35 + Math.random() * 0.9),
        r,
        life: 0,
        maxLife: 4200 + Math.random() * 3800,
        hue: 36 + Math.random() * 10,
        flicker: Math.random() * Math.PI * 2,
      });
    };

    let last = performance.now();
    let acc = 0;
    const spawnIntervalMs = 42;
    let raf = 0;

    const frame = (now: number) => {
      const dt = now - last;
      last = now;
      acc += dt;
      while (acc > spawnIntervalMs) {
        spawn();
        acc -= spawnIntervalMs;
      }

      ctx.clearRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;
      const glowR = Math.min(w, h) * 0.55;
      const pulse = 0.55 + 0.08 * Math.sin(now / 1600);
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      grad.addColorStop(0, `rgba(200, 173, 106, ${0.18 * pulse})`);
      grad.addColorStop(0.55, `rgba(200, 173, 106, ${0.05 * pulse})`);
      grad.addColorStop(1, 'rgba(200, 173, 106, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      for (let i = embers.length - 1; i >= 0; i--) {
        const e = embers[i];
        e.life += dt;
        if (e.life >= e.maxLife || e.y < -20) {
          embers.splice(i, 1);
          continue;
        }
        e.x += e.vx * (dt / 16);
        e.y += e.vy * (dt / 16);
        e.vx += (Math.random() - 0.5) * 0.01;
        e.flicker += 0.08;

        const p = e.life / e.maxLife;
        const fadeIn = Math.min(1, p * 6);
        const fadeOut = 1 - p;
        const alpha = Math.max(0, fadeIn * fadeOut) * (0.78 + 0.22 * Math.sin(e.flicker));
        const rr = e.r * (0.85 + 0.25 * Math.sin(e.flicker));

        const g = ctx.createRadialGradient(e.x, e.y, 0, e.x, e.y, rr * 5);
        g.addColorStop(0, `hsla(${e.hue}, 78%, 70%, ${alpha})`);
        g.addColorStop(0.35, `hsla(${e.hue}, 78%, 58%, ${alpha * 0.45})`);
        g.addColorStop(1, `hsla(${e.hue}, 78%, 50%, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(e.x, e.y, rr * 5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `hsla(${e.hue + 6}, 92%, 82%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(e.x, e.y, rr, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="ds-root ds-embers">
      <canvas ref={canvasRef} className="ds-canvas" aria-hidden />
      <div className="ds-vignette" aria-hidden />
      <div className="ds-center">
        <Typewriter startDelayMs={900} />
      </div>
    </div>
  );
}

const SHARED_CSS = `
  .ds-root {
    position: fixed;
    inset: 0;
    background: radial-gradient(ellipse at center, #0f0c08 0%, #060503 70%, #030201 100%);
    z-index: 9999;
    overflow: hidden;
    isolation: isolate;
  }
  .ds-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
  }
  .ds-vignette {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.55) 100%);
  }
  .ds-center {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    padding: 24px;
    z-index: 2;
  }
  .ds-text {
    display: flex;
    flex-direction: column;
    gap: 18px;
    color: #ede5d0;
    font-family: 'EB Garamond', 'Cormorant Garamond', Georgia, 'Times New Roman', serif;
    text-shadow: 0 0 22px rgba(200, 173, 106, 0.18);
    letter-spacing: 0.01em;
  }
  .ds-line {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: 1.2em;
  }
  .ds-l1 {
    font-size: clamp(28px, 5vw, 46px);
    font-weight: 500;
    color: #f1e9d4;
    opacity: 0.96;
  }
  .ds-l2 {
    font-size: clamp(18px, 2.6vw, 24px);
    font-weight: 400;
    color: rgba(233, 221, 191, 0.78);
    font-style: italic;
  }
  .ds-caret {
    display: inline-block;
    width: 2px;
    height: 1em;
    background: #c8ad6a;
    margin-left: 3px;
    box-shadow: 0 0 10px rgba(200, 173, 106, 0.8);
    animation: ds-blink 1s steps(2) infinite;
    vertical-align: -0.1em;
  }
  .ds-caret.hide { opacity: 0; animation: none; }
  .ds-caret.idle { animation: ds-blink 1.2s steps(2) infinite; }
  @keyframes ds-blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }

  .ds-signout-wrap {
    position: fixed;
    bottom: 28px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10000;
    opacity: 0;
    animation: ds-signout-in 800ms ease-out 4200ms forwards;
  }
  @keyframes ds-signout-in {
    from { opacity: 0; transform: translate(-50%, 6px); }
    to { opacity: 1; transform: translate(-50%, 0); }
  }
  .ds-signout {
    appearance: none;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(200, 173, 106, 0.28);
    color: rgba(233, 221, 191, 0.78);
    padding: 10px 22px;
    border-radius: 999px;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    letter-spacing: 0.4px;
    transition: color 180ms ease, border-color 180ms ease, background 180ms ease;
  }
  .ds-signout:hover {
    color: #f1e9d4;
    border-color: rgba(200, 173, 106, 0.55);
    background: rgba(200, 173, 106, 0.12);
  }
`;
